import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  PrometheusAggregateResponse,
  PrometheusMetricsData,
  PrometheusTopBottomAvgMetricResponse,
  PrometheusTopBottomValuesResponse,
} from "../../api/observeApi";
import {
  fetchPrometheusAggregate,
  fetchPrometheusMetric,
  fetchPrometheusTopBottomAvg,
  fetchPrometheusTopBottomValues,
} from "./deploymentPrometheusMetricsThunks";

export interface DeploymentPrometheusMetricsState {
  metricKey: string;
  prometheusMetricResults: {
    [metric: string]: PrometheusMetricsData;
  };
  prometheusMetricResultsV2: {
    [metric: string]: PrometheusTopBottomAvgMetricResponse;
  };
  prometheusTopBottomValues: {
    [metric: string]: PrometheusTopBottomValuesResponse;
  };
  prometheusAggregate: {
    [metric: string]: PrometheusAggregateResponse;
  };
  loadingPrometheusMetricResults: {
    [metric: string]: boolean;
  };
  loadingPrometheusMetricResultsV2: {
    [metric: string]: boolean;
  };
  loadingPrometheusTopBottomValues: {
    [metric: string]: boolean;
  };
  loadingPrometheusAggregate: {
    [metric: string]: boolean;
  };
  topNLimit: number;
  bottomNLimit: number;
}

const initialState: DeploymentPrometheusMetricsState = {
  metricKey: "",
  prometheusMetricResults: {},
  prometheusMetricResultsV2: {},
  prometheusTopBottomValues: {},
  prometheusAggregate: {},
  loadingPrometheusMetricResults: {},
  loadingPrometheusMetricResultsV2: {},
  loadingPrometheusTopBottomValues: {},
  loadingPrometheusAggregate: {},
  topNLimit: 10,
  bottomNLimit: 10,
};

export const deploymentPrometheusMetricsSlice = createSlice({
  name: "deploymentPrometheusMetricsSlice",
  initialState,
  reducers: {
    resetDepPromMetrics: () => initialState,
    setMetricKey: (state, action: PayloadAction<string>) => {
      state.metricKey = action.payload;
    },
    setTopNLimit: (state, action: PayloadAction<number>) => {
      state.topNLimit = action.payload;
    },
    setBottomNLimit: (state, action: PayloadAction<number>) => {
      state.bottomNLimit = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrometheusMetric.fulfilled, (state, action) => {
        state.loadingPrometheusMetricResults[action.meta.arg] = false;
        state.prometheusMetricResults[action.meta.arg] = action.payload;
      })
      .addCase(fetchPrometheusMetric.pending, (state, action) => {
        state.loadingPrometheusMetricResults[action.meta.arg] = true;
        state.prometheusMetricResults[action.meta.arg] = {
          top_bottom_n_value: 0,
          retrieved_at: [],
          avg_rows: [],
          metrics: {},
        };
      })
      .addCase(fetchPrometheusMetric.rejected, (state, action) => {
        state.loadingPrometheusMetricResults[action.meta.arg] = false;
        console.log("failed", action);
      })
      .addCase(fetchPrometheusTopBottomAvg.fulfilled, (state, action) => {
        state.loadingPrometheusMetricResultsV2[action.meta.arg] = false;
        state.prometheusMetricResultsV2[action.meta.arg] = action.payload;
      })
      .addCase(fetchPrometheusTopBottomAvg.pending, (state, action) => {
        state.loadingPrometheusMetricResultsV2[action.meta.arg] = true;
        state.prometheusMetricResultsV2[action.meta.arg] = {
          retrieved_at: [],
          avg: {},
          top_1: {},
          bottom_1: {},
        };
      })
      .addCase(fetchPrometheusTopBottomAvg.rejected, (state, action) => {
        state.loadingPrometheusMetricResultsV2[action.meta.arg] = false;
        console.log("failed", action);
      })
      .addCase(fetchPrometheusTopBottomValues.fulfilled, (state, action) => {
        state.loadingPrometheusTopBottomValues[action.meta.arg] = false;
        state.prometheusTopBottomValues[action.meta.arg] = action.payload;
      })
      .addCase(fetchPrometheusTopBottomValues.pending, (state, action) => {
        state.loadingPrometheusTopBottomValues[action.meta.arg] = true;
        state.prometheusTopBottomValues[action.meta.arg] = {
          top_labels: [],
          bottom_labels: [],
        };
      })
      .addCase(fetchPrometheusTopBottomValues.rejected, (state, action) => {
        state.loadingPrometheusTopBottomValues[action.meta.arg] = false;
        console.log("failed", action);
      })
      .addCase(fetchPrometheusAggregate.fulfilled, (state, action) => {
        state.loadingPrometheusAggregate[action.meta.arg] = false;
        state.prometheusAggregate[action.meta.arg] = action.payload;
      })
      .addCase(fetchPrometheusAggregate.pending, (state, action) => {
        state.loadingPrometheusAggregate[action.meta.arg] = true;
        state.prometheusAggregate[action.meta.arg] = {
          aggregate: {},
          retrieved_at: [],
        };
      })
      .addCase(fetchPrometheusAggregate.rejected, (state, action) => {
        state.loadingPrometheusAggregate[action.meta.arg] = false;
        console.log("failed", action);
      });
  },
});

export const {
  resetDepPromMetrics,
  setMetricKey,
  setTopNLimit,
  setBottomNLimit,
} = deploymentPrometheusMetricsSlice.actions;

export default deploymentPrometheusMetricsSlice.reducer;
